import * as React from "react";
import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Trans, useTranslation } from "react-i18next";
import useProductName from "brand-customization/useProductName";
import useStreamDataLakeStatusMutation from "data-lake/hooks/useStreamDataLakeStatusMutation";
import { ConfirmDialog, OverlayTrigger, Link } from "components/common";
import Popover from "components/common/Popover";
import { useDataLakeBackendConfig } from "data-lake/data-lake-backend-config/hooks/useDataLakeBackendConfig";
import DestinationSwitch from "components/streams/StreamDetails/routing-destination/DestinationSwitch";
import usePermissions from "hooks/usePermissions";
import useSendTelemetry from "logic/telemetry/useSendTelemetry";
import { TELEMETRY_EVENT_TYPE } from "telemetry/Constants";
import DataLakeRoutes from "data-lake/Routes";
import AppConfig from "util/AppConfig";
const StreamDataLakeEnableButton = ({ streamId, config }) => {
  const [showModal, setShowModal] = useState(false);
  const { onToggleStreamDataLakeConfigStatus } = useStreamDataLakeStatusMutation();
  const { data, isSuccess } = useDataLakeBackendConfig();
  const isDataLakeEnabled = (config == null ? void 0 : config.enabled) || false;
  const { t } = useTranslation("enterprise_data_lake");
  const title = isDataLakeEnabled ? t("enable_button.enabled") : t("enable_button.disabled");
  const activeBackendExists = isSuccess && data.active_backend;
  const queryClient = useQueryClient();
  const { isPermitted } = usePermissions();
  const sendTelemetry = useSendTelemetry();
  const productName = useProductName();
  const onModalClose = () => {
    setShowModal(false);
  };
  const onButtonClick = () => {
    setShowModal(true);
    sendTelemetry(TELEMETRY_EVENT_TYPE.DATALAKE.ENABLE_DATALAKE_CLICKED, {
      app_pathname: "data-lake"
    });
  };
  const onConfirm = () => {
    onToggleStreamDataLakeConfigStatus({ streamIds: [streamId], enabled: !isDataLakeEnabled }).then(() => {
      setShowModal(false);
      queryClient.invalidateQueries({
        queryKey: ["data-lake", streamId]
      });
      queryClient.invalidateQueries({
        queryKey: ["data-lake", "stream", streamId]
      });
    });
  };
  const popover = /* @__PURE__ */ React.createElement(Popover, null, t("enable_button.no_backend"), " ", AppConfig.isCloud() ? t("enable_button.cloud_setup", { productName }) : /* @__PURE__ */ React.createElement(
    Trans,
    {
      t,
      i18nKey: "enable_button.on_prem_setup",
      components: { backendLink: /* @__PURE__ */ React.createElement(Link, { to: DataLakeRoutes.BACKEND }) }
    }
  ));
  return /* @__PURE__ */ React.createElement(React.Fragment, null, showModal && /* @__PURE__ */ React.createElement(
    ConfirmDialog,
    {
      show: showModal,
      onConfirm,
      onCancel: onModalClose,
      title: isDataLakeEnabled ? t("enable_button.disable_title") : t("enable_button.enable_title")
    },
    /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(
      Trans,
      {
        t,
        i18nKey: isDataLakeEnabled ? "enable_button.confirm_disable" : "enable_button.confirm_enable",
        components: { bold: /* @__PURE__ */ React.createElement("strong", null) }
      }
    ), /* @__PURE__ */ React.createElement("p", null, t("enable_button.confirm_question")))
  ), activeBackendExists ? /* @__PURE__ */ React.createElement(
    DestinationSwitch,
    {
      "aria-label": t("enable_button.toggle_aria"),
      name: "toggle-data-lake",
      checked: isDataLakeEnabled,
      onChange: onButtonClick,
      disabled: !isPermitted(["data_lake_config:update", `streams:edit:${streamId}`]),
      label: title
    }
  ) : /* @__PURE__ */ React.createElement(OverlayTrigger, { placement: "top", trigger: "click", rootClose: true, overlay: popover }, /* @__PURE__ */ React.createElement(
    DestinationSwitch,
    {
      "aria-label": t("enable_button.toggle_aria"),
      name: "toggle-data-lake",
      disabled: true,
      onChange: () => {
      },
      checked: false,
      label: title
    }
  )));
};
export default StreamDataLakeEnableButton;
